// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNil, isUndefined, omitBy, isEmpty, set } from "lodash";
import moment, { Moment } from "moment";
import { useParams } from "react-router-dom";

// ANT COMPONENT
import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  AlertBox,
  BasicModal,
  Button,
  PhoneInput,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
import { Avatar, Checkbox, Col, Row, Spin, TimePicker } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Upload from "Components/shared/upload/upload";

// STYLE
import "./customer-profile-company-form.scss";
import "./../customer-management.scss";

// IMPORT TYPE
import {
  CustomerCompanyFormInputType,
  CustomerType,
  OrgType,
} from "./../../../types/customer-form.types";
import {
  Day,
  FilePayload,
  FileTypeFilePayload,
  OrgList,
  ShallowClonedTypeFor,
} from "./../../../types/common.types";

import {
  reverseGeocoding,
  validateGeocoding,
} from "Services/geocoding.services";

// HELPERS
import { toaster } from "Helpers/toaster";
import { getBase64 } from "Helpers/getbase64";

// UTILS
import errorHandler from "Utils/error-handler";

// HOOKS
import useCountriesList from "Hooks/countries-option.hook";
import useStatesList from "Hooks/states-option.hook";
import OperationalHoursComponent from "../customer-profile-shared/customer-profile-operation-hours";
import { OperationHoursTimeEnums } from "Data/order";
import CustomerProfileParcelTypes from "../customer-profile-shared/customer-profile-parcel";
import CustomerCompanyFormSchema from "./customer-profile-company-form.validation";

interface CustomerProfileCompanyFormComponentProps {
  onSubmit?: (data) => void;
  onSubmitEdit?: (data) => void;
  onBack?: () => void;
  customerDetails?: Partial<CustomerCompanyFormInputType>;
  orgList: OrgList[];
  isLoading?: boolean;
}

interface CustomerFormErrorType
  extends ShallowClonedTypeFor<
    Partial<CustomerCompanyFormInputType>,
    string | null
  > {
  general?: string;
}

interface OptionalType {
  [key: string]: any;
}

// =========================================================
//  Tracks working hours
// =========================================================
const initialWorkingHours = [
  {
    day: "MONDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "TUESDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "WEDNESDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "THURSDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "FRIDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "SATURDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "SUNDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
];

const useDaysChecked = (input, setInput, error, setError) => {
  const modifyState = (currentInput) => {
    setInput((prevInput) => ({ ...prevInput, workingHours: currentInput }));
  };

  const setDays = (e, day) => {
    setError({ ...error, workingHours: null });
    const dayIsChecked = e.target.checked;

    //  if the everyday checkbox is checked, set all isCheck to true
    // ================================================================
    if (day == Day.everyday) {
      if (dayIsChecked) {
        let workingHours = input?.workingHours?.map((current) => {
          return {
            ...current,
            isChecked: true,
          };
        });

        modifyState(workingHours);
      } else {
        //  if the everyday checkbox is unchecked, set all isCheck to false and clear time
        // =================================================================================
        let workingHours = input?.workingHours?.map((current) => {
          return {
            ...current,
            openingTime: null,
            closingTime: null,
            isChecked: false,
          };
        });

        modifyState(workingHours);
      }
    } else {
      //  if a day is checked, set that day's isCheck to true
      // =========================================================================
      if (dayIsChecked) {
        let workingHours = input?.workingHours?.map((current) => {
          if (day == current?.day) {
            return {
              ...current,
              isChecked: true,
            };
          } else {
            return current;
          }
        });

        modifyState(workingHours);
      } else if (!dayIsChecked) {
        //  if a day is unchecked, set that day's isCheck to false and clear time
        // =========================================================================
        let workingHours = input?.workingHours?.map((current) => {
          if (day == current?.day) {
            return {
              ...current,
              openingTime: null,
              closingTime: null,
              isChecked: false,
            };
          } else {
            return current;
          }
        });

        modifyState(workingHours);
      }
    }
  };

  return {
    setDays,
  };
};

// =========================================================
//  Form Handlers
// =========================================================
const useFormHandlers = (props) => {
  const {
    input,
    error,
    prevImg,
    initialError,
    avatarImg,
    setAvatarImg,
    setModal,
    setPrevImg,
    setDays,
    setIndeterminate,
    setIsAllSelected,
    setInput,
    setError,
  } = props || {};

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setError({ ...error, acceptTerms: null });
    setInput({ ...input, acceptTerms: e.target.checked });
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // To implement checking of prefixes once API is ready
    setError({ ...error, prefix: null });
    setInput({ ...input, prefix: e.target.value });
  };

  const handlePhoneChange = (
    value: { dialCode: string; number: string },
    name: string,
  ) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleImageChange = ({ fileList: fileList }) => {
    setAvatarImg(fileList);
  };

  const handleImageRemove = () => {
    setAvatarImg([]);
    const fileURL = prevImg?.file_url || "";
    if (fileURL) {
      setInput({ ...input, file: prevImg });
    } else {
      setInput({ ...input, file: undefined });
    }
  };

  const handleConfirmImage = () => {
    setModal(false);
    const fileURL = input.file?.file_url || "";
    if (fileURL && input.file) {
      setPrevImg(input.file);
    }
    avatarImg.forEach(async (fileObj: any) => {
      if (fileObj?.fileID == undefined) {
        let base64Data: any = "";
        base64Data = await getBase64(fileObj.originFileObj);
        setInput({
          ...input,
          file: {
            fileName: fileObj.name,
            fileType: FileTypeFilePayload.IMAGE,
            base64String: base64Data,
          },
        });
      }
    });
  };

  const handleTimeChange = (
    time: Moment | null,
    day: Day,
    type: OperationHoursTimeEnums,
  ) => {
    // clear error
    setError({ ...error, workingHours: null });

    // sets the time in which the day is checked
    // if openingTime is passed, set openingTime, otherwise set closingTime
    // when one is set, the other takes either existing value or null
    setInput((prevInput) => ({
      ...prevInput,
      workingHours: prevInput?.workingHours?.map((current) => {
        if (current?.day == day) {
          return {
            ...current,
            openingTime:
              type == "openingTime" ? time : current?.openingTime ?? null,
            closingTime:
              type == "closingTime" ? time : current?.closingTime ?? null,
          };
        } else {
          return current;
        }
      }),
    }));
  };

  const handleWorkingHourChange = (e: CheckboxChangeEvent, day: Day) => {
    let numberOfChecks = 0;

    // count the number of checked boxes
    // 7 because there are 7 possible working days (Monday -> Sunday)
    for (let i = 0; i < 7; i++) {
      if (input?.workingHours && input?.workingHours[i]?.isChecked) {
        numberOfChecks++;
      }
    }

    // if the day is checked, but not select all, add into numberOfChecks
    if (day !== Day.everyday && e.target.checked) {
      numberOfChecks++;
    }

    // if selectAll is checked by user OR numberOfChecks = 7, check the selectAll box
    if (day === Day.everyday || numberOfChecks == 7) {
      setIndeterminate && setIndeterminate(false);

      if (e.target.checked) setIsAllSelected && setIsAllSelected(true);
      else setIsAllSelected && setIsAllSelected(false);
    } else {
      // assume at least one is checked (cant be 7 because that is handled in the above if)
      setIndeterminate && setIndeterminate(true);
      // unset indeterminate if numberOfChecks is 0
      if (numberOfChecks === 0) setIndeterminate && setIndeterminate(false);
    }

    setDays && setDays(e, day);
  };

  const handleCoordinatesChecking = async (address: any) => {
    const responseData = await reverseGeocoding(address);
    if (responseData && !responseData.error) {
      return responseData.coordinates;
    } else {
      setError({ ...error, country: "Address does not exist" });
    }
  };

  const handleValidateAddress = async (address: any) => {
    const responseData = await validateGeocoding(address);
    if (responseData && !responseData.error) {
      if (responseData?.verdict) return true;
      else return false;
    } else {
      return false;
    }
  };

  return {
    handleFieldChange,
    handleCheckboxChange,
    handleImageChange,
    handleWorkingHourChange,
    handlePhoneChange,
    handlePrefixChange,
    handleImageRemove,
    handleTimeChange,
    handleConfirmImage,
    handleSelectChange,
    handleCoordinatesChecking,
    handleValidateAddress,
  };
};

// =========================================================
//  Operational Hours Component
// =========================================================

// =========================================================
//  Main Component
// =========================================================
//  Initial Input
// ===================================
const initialInput: Partial<CustomerCompanyFormInputType> & OptionalType = {
  acceptTerms: false,
  orgType: OrgType.CUSTOMER,
  customerType: CustomerType.COMPANY,
  contactNo: {
    dialCode: "60",
    number: "",
  },
  adminContactNo: {
    dialCode: "60",
    number: "",
  },
  officeContactNo: {
    dialCode: "60",
    number: "",
  },
  workingHours: [...initialWorkingHours],
  allowPickupAll: true
};

const initialError = {};
const initialPrevImg: FilePayload = {
  fileName: "",
  fileType: FileTypeFilePayload.IMAGE,
  base64String: "",
};

const CustomerProfileCompanyFormComponent: React.FC<
  CustomerProfileCompanyFormComponentProps
> = ({
  onSubmit,
  onSubmitEdit,
  onBack,
  customerDetails,
  orgList,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  //  All states
  // ===================================
  const [input, setInput] =
    useState<Partial<CustomerCompanyFormInputType>>(initialInput);
  const [error, setError] = useState<CustomerFormErrorType>(initialError);
  const [modal, setModal] = useState<boolean>(false);
  const [avatarImg, setAvatarImg] = useState<any>([]);
  const [prevImg, setPrevImg] = useState<FilePayload>(initialPrevImg);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  //  Hooks
  // ===================================
  const stateOption = useStatesList(input?.country);
  const countryOption = useCountriesList();

  const { setDays } = useDaysChecked(input, setInput, error, setError);

  const formHandlerProps = {
    input,
    error,
    prevImg,
    initialError,
    avatarImg,
    setAvatarImg,
    setModal,
    setPrevImg,
    setDays,
    setIndeterminate,
    setIsAllSelected,
    setInput,
    setError,
  };

  const {
    handleFieldChange,
    handleCheckboxChange,
    handleImageChange,
    handleWorkingHourChange,
    handlePhoneChange,
    handlePrefixChange,
    handleImageRemove,
    handleTimeChange,
    handleConfirmImage,
    handleSelectChange,
    handleCoordinatesChecking,
    handleValidateAddress,
  } = useFormHandlers({ ...formHandlerProps });

  //  Populate Customer Details
  // ===================================
  useEffect(() => {
    if (customerDetails) {
      // map response to workingHours
      customerDetails?.workingHours?.map((workingHour) => {
        let tempHours = {
          day: workingHour?.day,
          openingTime: moment(workingHour?.openingTime, "YYYY-MM-DD hh:mm a Z"),
          closingTime: moment(workingHour?.closingTime, "YYYY-MM-DD hh:mm a Z"),
          isChecked: true,
        };

        // replace appropriately
        setInput((prevInput) => ({
          ...prevInput,
          workingHours: prevInput?.workingHours?.map((hour) => {
            if (hour?.day == workingHour?.day) {
              return tempHours;
            } else {
              return hour;
            }
          }),
        }));
      });

      // check if it is undefined
      if (customerDetails?.workingHours) {
        // if company has all working hours, check the select all box
        if (customerDetails?.workingHours?.length == 7)
          setIsAllSelected && setIsAllSelected(true);
        // if company has at least one, set indeterminate
        else if (customerDetails?.workingHours?.length > 0)
          setIndeterminate && setIndeterminate(true);
      }

      const { workingHours, ...otherDetails } = customerDetails;

      // add the rest of the details
      setInput((prevInput) => ({
        ...prevInput,
        ...otherDetails,
      }));
    }
  }, [customerDetails]);

  const miniAddressNormaliser = (data, omitStreet = false) => {
    return omitBy(
      {
        street1: omitStreet ? undefined : data.street1,
        street2: omitStreet ? undefined : data.street2,
        city: data.city,
        state: data.state,
        postcode: data.postcode,
        country: data.country,
      },
      isUndefined,
    );
  };

  //  Submit
  // ===================================
  const handleSubmit = async () => {
    try {
      const value = CustomerCompanyFormSchema.validateSync(
        omitBy(
          {
            ...input,
            customerName: input?.customerName?.trim(),
            contactPerson: input?.contactPerson?.trim(),
          },
          isUndefined,
        ),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
          context: { data: { ...input, indeterminate } },
        },
      );

      if (!isEmpty(value)) {
        setIsLoadingSubmit && setIsLoadingSubmit(true);
        const addressToCheck = miniAddressNormaliser(input, true);
        const addressValidity = await handleValidateAddress(addressToCheck);

        if (addressValidity) {
          const addressToCheck = miniAddressNormaliser(input);
          const coordinatesObtained = await handleCoordinatesChecking(
            addressToCheck,
          );

          const finalValue = { ...value, coordinates: coordinatesObtained };

          if (input._id) {
            if (onSubmitEdit) {
              onSubmitEdit(finalValue);
            }
          } else {
            if (onSubmit) {
              onSubmit(finalValue);
            }
          }
        } else {
          toaster("error", t("error.addressNotFound"));
        }
      } else {
        setError({ ...error, general: t("error.validationFailed") });
      }

      setIsLoadingSubmit && setIsLoadingSubmit(false);
    } catch (err) {
      let { path, value, message }: any = errorHandler.validation(err);
      console.log(path, value, message);

      // if path is an array
      let isPathArray = false;
      let errorObj = {};

      // error for workingHours
      if (path.indexOf("[") !== -1) {
        isPathArray = true;
        let checkValue = true;
        const errorPath = path.split("[");

        switch (message) {
          case "beforeOpeningTime":
            checkValue = false;
            errorObj = {
              ...error,
              [errorPath[0]]: t(`error.beforeOpeningTime`, {
                field: t(`label.${path}`),
              }),
            };
            break;
          case "workingHoursRequired":
            checkValue = false;
            errorObj = {
              ...error,
              [errorPath[0]]: t(`error.workingHoursRequired`),
            };
            break;
          default:
        }

        if (checkValue) {
          switch (value) {
            case "error.pattern":
              errorObj = {
                ...error,
                [errorPath[0]]: t(`error.pattern`, {
                  field: t(`label.${path}`),
                }),
              };
              break;
            case "error.validationFailed":
              errorObj = {
                ...error,
                [errorPath[0]]: t(`error.required`, {
                  field: t(`label.${path}`),
                }),
              };
              break;
            default:
              errorObj = {
                ...error,
                [errorPath[0]]: t(`${value}`, { field: t(`label.${path}`) }),
              };
          }
        }
      }

      // e.g. contactNumber.number = contactNumber
      if (path.indexOf(".") !== -1) {
        const str = path.split(".");
        path = str[0];
      }

      if (!isPathArray) {
        // validation message
        switch (value) {
          case "error.pattern":
            errorObj = {
              [path]: t(`error.pattern`, { field: t(`label.${path}`) }),
            };
            break;
          case "error.validationFailed":
            errorObj = {
              [path]: t(`error.required`, { field: t(`label.${path}`) }),
            };
            break;
          default:
            errorObj = {
              [path]: t(`${value}`, { field: t(`label.${path}`) }),
            };
        }

        if (document.querySelector(`input[name=${path}]`)) {
          (
            document.querySelector(`input[name=${path}]`) as HTMLInputElement
          ).focus();
        } else if (document.querySelector(`div[name=${path}]`)) {
          (
            document.querySelector(
              `div[name=${path}] input`,
            ) as HTMLInputElement
          ).focus();
        }
      }

      setIsLoadingSubmit && setIsLoadingSubmit(false);

      // general message
      errorObj = {
        ...errorObj,
        general: t("txt.resolveFormError"),
      };

      setError((prev) => ({ ...prev, ...errorObj }));
    }
  };

  const sharedFormProps = {
    input,
    error,
    handleChange: handleFieldChange,
    handleSelectChange: handleSelectChange,
  };

  //  Render
  // ===================================
  return (
    <div
      className={`
            customer-management customer-profile-edit-details-container
            ${input._id ? "edit-form" : ""}`}
    >
      {isLoading || isLoadingSubmit ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <form>
          <div className="cm-form-section">
            <div className="cm-form-title">
              {input._id ? (
                <span
                  className="cursor-pointer back-action"
                  onClick={() => {
                    onBack ? onBack() : {};
                  }}
                >
                  <ArrowLeftOutlined />
                  <span className="ml-5">{t("txt.back")}</span>
                </span>
              ) : null}
              <span className="header-title">
                {t("title.customerDetails").toUpperCase()}
              </span>
            </div>
            <div className="cm-form-img">
              <div
                onClick={() => {
                  setModal(true);
                }}
                className="pointer"
              >
                <Avatar
                  size={100}
                  src={input.file?.base64String || input.file?.file_url}
                  icon={<UserOutlined style={{ fontSize: 100 }} />}
                />
              </div>
            </div>
            <div className="cm-form-content">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12}>
                  <TextInput
                    isRequired={true}
                    label={t("label.customerName").toUpperCase()}
                    placeholder={t("txt.customerNameExample")}
                    value={input.customerName}
                    name="customerName"
                    onChange={handleFieldChange}
                    isReadonly={input._id ? true : false}
                  />
                  <AlertBox text={error.customerName} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <TextInput
                    isRequired={true}
                    label={t("label.businessRegistrationNumber")}
                    placeholder={t("txt.businessRegistrationNumberExample")}
                    value={input.businessRegistrationNumber}
                    name="businessRegistrationNumber"
                    onChange={handleFieldChange}
                    isReadonly={input._id ? true : false}
                  />
                  <AlertBox text={error.businessRegistrationNumber} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <PhoneInput
                    isRequired={true}
                    isError={!!error.contactNo}
                    label={t("label.contactNo")}
                    name="contactNo"
                    value={input.contactNo}
                    onChange={(value) => handlePhoneChange(value, "contactNo")}
                  />
                  <AlertBox text={error.contactNo} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <SelectInput
                    showSearch
                    options={orgList.map((org) => {
                      return {
                        label: org.orgName || "",
                        value: org.orgID,
                      };
                    })}
                    filterOption={(inputValue, option) =>
                      option.label
                        .toLowerCase()
                        .indexOf(inputValue.toLowerCase()) >= 0
                    }
                    value={input.parentOrgID}
                    isRequired={true}
                    isReadonly={input._id ? true : false}
                    name="parentOrgID"
                    label={t("label.diolkoCompany")}
                    onChange={(value: string | string[]) =>
                      handleSelectChange(value, "parentOrgID")
                    }
                  />
                  <AlertBox text={error.parentOrgID} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <label className="inp-label inp-label--required">
                    {t("label.registeredAddress").toUpperCase()}
                  </label>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      placeholder={t("txt.street1Example")}
                      value={input.street1}
                      name="street1"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.street1} />
                  </div>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      placeholder={t("txt.street2Example")}
                      value={input.street2}
                      name="street2"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.street2} />
                  </div>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <TextInput
                        isRequired={true}
                        placeholder={t("txt.postcode")}
                        value={input.postcode}
                        name="postcode"
                        onChange={handleFieldChange}
                      />
                      <AlertBox text={error.postcode} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <TextInput
                        isRequired={true}
                        placeholder={t("txt.city")}
                        value={input.city}
                        name="city"
                        onChange={handleFieldChange}
                      />
                      <AlertBox text={error.city} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <SelectInput
                        showSearch
                        options={stateOption}
                        value={input.state}
                        isReadonly={isNil(input.country)}
                        filterOption={(inputValue, option) =>
                          option.label
                            .toLowerCase()
                            .indexOf(inputValue.toLowerCase()) >= 0
                        }
                        name="state"
                        placeholder={t("option.state")}
                        onChange={(value: string | string[]) =>
                          handleSelectChange(value, "state")
                        }
                      />
                      <AlertBox text={error.state} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <SelectInput
                        showSearch
                        filterOption={(inputValue, option) =>
                          option.label
                            .toLowerCase()
                            .indexOf(inputValue.toLowerCase()) >= 0
                        }
                        options={countryOption}
                        value={input.country}
                        name="country"
                        placeholder={t("option.country")}
                        onChange={(value: string | string[]) =>
                          handleSelectChange(value, "country")
                        }
                      />
                      <AlertBox text={error.country} />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      label={t("label.emailAddress").toUpperCase()}
                      placeholder={t("txt.emailAddressExample")}
                      value={input.emailAddress}
                      name="emailAddress"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.emailAddress} />
                  </div>
                  <div className="mb-15">
                    <TextInput
                      placeholder={t("txt.companyWebsiteExample")}
                      value={input.companyWebsite}
                      name="companyWebsite"
                      label={t("label.companyWebsite").toUpperCase()}
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.companyWebsite} />
                  </div>
                  <div className="mb-15">
                    <TextInput
                      showCount
                      isRequired={true}
                      value={input.prefix}
                      maxLength={3}
                      isReadonly={input._id ? true : false}
                      name="prefix"
                      label={t("label.prefix")}
                      onChange={handlePrefixChange}
                    />
                    <AlertBox text={error.prefix} />
                  </div>
                </Col>
                <Col xs={24} sm={24}>
                  <OperationalHoursComponent
                    indeterminate={indeterminate}
                    isAllSelected={isAllSelected}
                    input={input}
                    error={error}
                    handleWorkingHourChange={handleWorkingHourChange}
                    handleTimeChange={handleTimeChange}
                  />
                </Col>
              </Row>
            </div>
          </div>
          <div className="cm-form-section">
            <div className="cm-form-title">
              <div className="header-title">
                {t("title.customerAdminDetails").toUpperCase()}
              </div>
            </div>
            <div className="cm-form-content">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12}>
                  <TextInput
                    isRequired={true}
                    label={t("label.contactPerson").toUpperCase()}
                    placeholder={t("txt.contactPersonExample")}
                    value={input.contactPerson}
                    name="contactPerson"
                    onChange={handleFieldChange}
                  />
                  <AlertBox text={error.contactPerson} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <TextInput
                    isRequired={true}
                    label={t("label.emailAddress").toUpperCase()}
                    placeholder={t("txt.emailAddressExample")}
                    value={input.adminEmailAddress}
                    name="adminEmailAddress"
                    onChange={handleFieldChange}
                  />
                  <AlertBox text={error.adminEmailAddress} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <PhoneInput
                    isRequired={true}
                    isError={!!error.adminContactNo}
                    label={t("label.contactNo")}
                    name="adminContactNo"
                    value={input.adminContactNo}
                    onChange={(value) =>
                      handlePhoneChange(value, "adminContactNo")
                    }
                  />
                  <AlertBox text={error.adminContactNo} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <PhoneInput
                    isError={!!error.officeContactNo}
                    label={t("label.officeContactNo").toUpperCase()}
                    name="officeContactNo"
                    value={input.officeContactNo}
                    onChange={(value) =>
                      handlePhoneChange(value, "officeContactNo")
                    }
                  />
                  <AlertBox text={error.officeContactNo} />
                </Col>
              </Row>
            </div>
          </div>
          <div className="cm-form-section">
            <div className="cm-form-title">
              <div className="header-title">
                {t("title.parcelSettings").toUpperCase()}
              </div>
            </div>
            <div className="cm-form-content">
              <CustomerProfileParcelTypes {...sharedFormProps} />
            </div>
          </div>
          {input._id ? null : (
            <div>
              <Checkbox
                checked={input.acceptTerms}
                onChange={handleCheckboxChange}
              >
                {t("txt.tnc")}
              </Checkbox>
              <AlertBox text={error.acceptTerms} />
            </div>
          )}
          {error?.general && (
            <div className="u-info u-error">{error.general}</div>
          )}
          <div className="cm-form-btn">
            <Button
              label={input._id ? t("action.save") : t("action.submit")}
              type="primary"
              onClick={() => {
                handleSubmit();
              }}
            />
          </div>
        </form>
      )}
      <BasicModal
        isOpen={modal}
        isClosable={false}
        onClose={() => {
          setModal(false);
        }}
        isMaskClosable={false}
        title={t("label.uploadProfilePicture")}
        bodyStyle={{ width: "70vw", maxWidth: "500px", margin: "0 auto" }}
      >
        <Upload
          onCancel={() => {
            setModal(false);
          }}
          onConfirm={() => {
            handleConfirmImage();
          }}
          onChange={handleImageChange}
          onRemove={handleImageRemove}
          fileList={avatarImg}
          uploadText={
            <div className="p-10">
              <p className="ant-upload-text">{t("txt.uploadImgFile")}</p>
              <p className="ant-upload-text">{t("txt.uploadFormat")}</p>
            </div>
          }
        />
      </BasicModal>
    </div>
  );
};

export default CustomerProfileCompanyFormComponent;
